import type { AdminAiScanFindingModalProps } from "../../models/admin-ai-scans.models";
import { adminAiScansStyles } from "../../styles/admin-ai-scans.styles";
import { ADMIN_AI_SCAN_SEVERITY_LABELS, ADMIN_AI_SCAN_TONE_CLASSES } from "../../utils/admin-ai-scans.constants";
import {
  getAdminAiScanFindingScore,
  getAdminAiScanFindingSeverity,
  getAdminAiScanFindingTitle,
} from "../../utils/admin-ai-scans.utils";
import AdminAiScanFindingDetails from "./AdminAiScanFindingDetails";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useRef } from "react";
import { twMerge } from "tailwind-merge";

// Reuse the existing admin dialog pattern for focus trapping, Escape, backdrop dismissal and focus restoration.
const AdminAiScanFindingModal = ({ finding, findingFields, source, onClose }: AdminAiScanFindingModalProps) => {
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const severity = getAdminAiScanFindingSeverity(finding);
  const score = getAdminAiScanFindingScore(finding);

  return (
    <Dialog
      open
      onClose={onClose}
      initialFocus={titleRef}
      className="relative z-[1000]"
      data-test="admin-ai-scan-finding-modal"
    >
      <div className="fixed inset-0 bg-slate-950/55" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-6">
        <Dialog.Panel className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white font-sans text-slate-900 shadow-2xl dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
          <header className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 bg-slate-50 px-5 py-4 dark:border-gray-600 dark:bg-gray-900/40">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-3">
                <span className={twMerge(adminAiScansStyles.severityBadge, ADMIN_AI_SCAN_TONE_CLASSES[severity])}>
                  {ADMIN_AI_SCAN_SEVERITY_LABELS[severity]}
                </span>
                <span className="text-sm font-semibold tabular-nums">
                  Risk {score === null ? "unspecified" : score.toFixed(2)}
                </span>
              </div>
              <Dialog.Title
                ref={titleRef}
                tabIndex={-1}
                className="break-words text-lg font-bold leading-7 outline-none"
              >
                {getAdminAiScanFindingTitle(finding)}
              </Dialog.Title>
              {source && (
                <p className="mt-1 break-words text-sm text-slate-600 dark:text-gray-300">
                  {source.moduleLabel} / {source.businessObjectLabel}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close finding details"
              data-test="admin-ai-scan-finding-modal-close"
              className="rounded p-2 text-slate-600 hover:bg-slate-200 focus-visible:outline-green dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </header>
          <div
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
            data-test="admin-ai-scan-finding-modal-body"
          >
            <AdminAiScanFindingDetails finding={finding} findingFields={findingFields} source={source} />
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default AdminAiScanFindingModal;
